import { BehaviorSubject, Observable } from 'rxjs';

export class PagedRecords<T> {
  private _records: T[];
  private _pageSize: number;
  private _pageNumber = 1;
  private _currentPage: T[] = [];
  private numberOfPagesSubject$: BehaviorSubject<number>;
  private pageNumberSubject$: BehaviorSubject<number>;
  private currentPageSubject$: BehaviorSubject<T[]>;

  set records(r: T[]) {
    this._records = r;
    // reset pages if recordset changes e.g. after filtering
    this.resetRecords();
  }
  get records(): T[] {
    return this._records;
  }

  set pageSize(size: number) {
    this._pageSize = size;
    this.resetRecords();
  }

  // For now, assume pageSize is set initially and never changes
  get pageSize(): number {
    return this._pageSize;
  }

  get numberOfPages(): number {
    return Math.ceil(this.records.length / this.pageSize);
  }
  get numberOfPages$(): Observable<number> {
    return this.numberOfPagesSubject$.asObservable();
  }

  // NB: this starts with page 1. Not zero-based!
  set pageNumber(n: number) {
    // safe-check, if requested page number is greater than actual paginated page numbers.
    // if yes, than make it to first page.
    if (n > this.numberOfPages) {
      n = 1;
    }
    this._pageNumber = n;
    this._calcPage(n);
    this.pageNumberSubject$.next(n);
    this.currentPageSubject$.next(this.currentPage);
  }
  get pageNumber(): number {
    return this._pageNumber;
  }
  get pageNumber$(): Observable<number> {
    return this.pageNumberSubject$.asObservable();
  }

  get currentPage(): T[] {
    return this._currentPage;
  }

  get currentPage$(): Observable<T[]> {
    return this.currentPageSubject$.asObservable();
  }

  // NB: pageSize is required in the constructor
  constructor(pageSize: number, records: T[] = []) {
    this.pageNumberSubject$ = new BehaviorSubject(this.pageNumber);
    this.currentPageSubject$ = new BehaviorSubject(this.currentPage);
    this._records = records;
    this._pageSize = pageSize;
    this.numberOfPagesSubject$ = new BehaviorSubject(this.numberOfPages);
    this.resetRecords();
  }

  public resetRecords(): void {
    this.pageNumber = 1;
    this.numberOfPagesSubject$.next(this.numberOfPages);
    this._calcPage(this.pageNumber);
  }
  public expandAll(): number {
    // this is set to a really big number, so there should only ever be a single page
    this.pageSize = 100000;
    this.resetRecords();
    return this.pageSize;
  }

  private _calcPage(n: number): void {
    const start: number = (n - 1) * this.pageSize;
    const end: number = Math.min(start + this.pageSize, this.records.length);
    this._currentPage = this.records.slice(start, end);
  }
}
